import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Upload,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const LeadImport = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [csvContent, setCsvContent] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setCsvContent(event.target.result);
      setResult(null);
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/leads/import/template', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'lead_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!csvContent.trim()) {
      toast.error('Please upload or paste CSV content');
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const response = await api.post('/leads/import', null, {
        params: { file_content: csvContent }
      });
      setResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} leads`);
      }
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} rows failed to import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="lead-import-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/leads')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            Import Leads
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Bulk import leads from CSV file
          </p>
        </div>
      </div>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="font-medium">Required Fields:</h4>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                <li>• customer_name</li>
                <li>• phone</li>
                <li>• email</li>
                <li>• property_address</li>
                <li>• city</li>
                <li>• state (2-letter code)</li>
                <li>• zip_code</li>
              </ul>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">Optional Fields:</h4>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
                <li>• lead_type (Residential/Commercial)</li>
                <li>• system_size_kw</li>
                <li>• notes</li>
                <li>• lead_source</li>
                <li>• is_dc_market (true/false)</li>
                <li>• is_free_dc_solar (true/false)</li>
              </ul>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Download CSV Template
          </Button>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Upload className="w-5 h-5 text-emerald-500" />
            Upload CSV File
          </CardTitle>
          <CardDescription>
            Upload a CSV file or paste the content directly
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-file-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Choose CSV File
            </Button>
            <span className="text-sm text-slate-500">or paste CSV content below</span>
          </div>

          <Textarea
            value={csvContent}
            onChange={(e) => {
              setCsvContent(e.target.value);
              setResult(null);
            }}
            placeholder="customer_name,phone,email,property_address,city,state,zip_code,lead_type,system_size_kw&#10;John Smith,(555) 123-4567,john@example.com,123 Main St,Phoenix,AZ,85001,Residential,10.5"
            rows={10}
            className="font-mono text-sm"
            data-testid="csv-content-input"
          />

          <div className="flex justify-end">
            <Button
              onClick={handleImport}
              disabled={importing || !csvContent.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="import-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Import Leads
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card data-testid="import-results">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              {result.failed === 0 ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-500" />
              )}
              Import Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center">
                <p className="text-3xl font-bold text-slate-900 dark:text-white">
                  {result.total_rows}
                </p>
                <p className="text-sm text-slate-500">Total Rows</p>
              </div>
              <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                <p className="text-3xl font-bold text-emerald-600">
                  {result.imported}
                </p>
                <p className="text-sm text-emerald-600">Imported</p>
              </div>
              <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                <p className="text-3xl font-bold text-red-600">
                  {result.failed}
                </p>
                <p className="text-sm text-red-600">Failed</p>
              </div>
            </div>

            {result.errors && result.errors.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-red-600">Errors:</h4>
                <div className="max-h-60 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Row</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.errors.map((error, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Badge variant="outline">Row {error.row}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-red-600">
                            {error.error}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {result.imported > 0 && (
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => navigate('/leads')}
                  data-testid="view-leads-btn"
                >
                  View Imported Leads
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeadImport;
